import { AdminPayment, PaymentRefund } from '../types/api';

// A Payment's refund targets, as sb-express-app's refundTargets and
// targetState (utils/PaymentAdminRefund.js, utils/PaymentReads.js) judge
// them: each Razorpay payment on the Payment — the main one, then any late
// or duplicate one — is one target, and one admin refund call acts on
// exactly one. Keep this in step with the backend.

// Mirrors the backend's STUCK_REFUND_MS (utils/PaymentRefund.js).
export const STUCK_REFUND_MS = 10 * 60 * 1000;

// 'none' means no refund attempt yet; 'stuck' is a pending attempt with no
// Razorpay refund id for over 10 minutes, which an admin can retry.
export type RefundTargetState = 'none' | 'stuck' | PaymentRefund['status'];

export interface RefundTarget {
  razorpay_payment_id: string;
  state: RefundTargetState;
}

const isStuck = (entry: PaymentRefund, now: Date): boolean =>
  entry.status === 'pending' &&
  !entry.razorpay_refund_id &&
  now.getTime() - new Date(entry.requested_at).getTime() > STUCK_REFUND_MS;

// Refunded if any attempt was processed, otherwise the latest attempt's status.
const targetState = (entries: PaymentRefund[], now: Date): RefundTargetState => {
  if (entries.length === 0) return 'none';
  if (entries.some((r) => r.status === 'processed')) return 'processed';
  const latest = entries.reduce((a, b) => (b.attempt > a.attempt ? b : a));
  return isStuck(latest, now) ? 'stuck' : latest.status;
};

// `now` decides which pending attempts are stuck.
export const refundTargets = (payment: AdminPayment, now: Date): RefundTarget[] => {
  const ids = [payment.razorpay_payment_id, ...payment.refunds.map((r) => r.razorpay_payment_id)].filter(
    (id): id is string => Boolean(id),
  );
  return Array.from(new Set(ids)).map((id) => ({
    razorpay_payment_id: id,
    state: targetState(payment.refunds.filter((r) => r.razorpay_payment_id === id), now),
  }));
};

// Integer paise as rupees, e.g. 50000 → ₹500.00.
export const formatPaise = (paise: number): string => `₹${(paise / 100).toFixed(2)}`;
